// ---------------------------------------------------------------------------
// Reviews Grid Renderer
// ---------------------------------------------------------------------------
// Pulls **published, approved** reviews from the unified review database
// (joined with `reviewWidgetReviewDecisions`) and renders them as a static
// grid on the published page. The renderer itself stays pure HTML — props are
// hydrated upstream by `loadPublishedReviewsForWidget` (services) so that the
// document renderer keeps the same prop-stamp contract as every other block.
//
// Distinct from `testimonial-grid`:
//   - testimonial-grid takes static, manually edited testimonials in the
//     builder.
//   - reviews-grid takes a curated subset of *real* reviews surfaced by
//     tenant moderation decisions, so the live page tracks reputation in
//     near-real-time without the editor re-typing them.
//
// Trust-zero contract:
//   - Renderer NEVER queries the DB; it only stamps props.
//   - Empty / unapproved review sets render an explicit empty state — no
//     silent block disappearance, so layout stays stable.
//   - Tenant identity is carried via `data-ease-tenant-id` on the section so
//     analytics + downstream MCP tools can scope to the same tenant the
//     loader read from.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

/**
 * One review surfaced to the public page. Mirrors the shape returned by
 * `loadPublishedReviewsForWidget` (which itself joins `reviews` with
 * `reviewWidgetReviewDecisions` filtered to status='approved').
 */
export interface PublishedReviewItem {
  id: string;
  authorName?: string;
  text?: string;
  rating: number;
  publishedAt?: string;
  sourceType?: string;
}

export interface ReviewsGridProps {
  heading?: string;
  reviews: PublishedReviewItem[];
  columns?: number;
  showRating?: boolean;
  showSource?: boolean;
  emptyStateMessage?: string;
  /**
   * Tenant identifier — written to a data-attribute for analytics / MCP scoping.
   * The loader sets this from the same `tenantId` it scoped its query to.
   */
  tenantId?: string;
}

const DEFAULT_HEADING = "What Customers Are Saying";
const DEFAULT_EMPTY_STATE = "No customer reviews yet — check back soon.";
const MIN_COLUMNS = 1;
const MAX_COLUMNS = 4;
const DEFAULT_COLUMNS = 3;

function clampRating(rating: number): number {
  if (!Number.isFinite(rating)) return 0;
  return Math.max(0, Math.min(5, Math.round(rating)));
}

function renderStars(rating: number): string {
  const clamped = clampRating(rating);
  const filled = "★".repeat(clamped);
  const empty = "☆".repeat(5 - clamped);
  return `<span class="ease-review-rating" aria-label="${attr(String(clamped))} out of 5 stars">${filled}${empty}</span>`;
}

function renderSourceBadge(sourceType: string): string {
  // Human-readable label for the source plugin. Schema enum is
  // ["google_business_profile", "facebook_pages"] today; keep render tolerant
  // of future plugins by titling unknown values via best-effort prettifying.
  const label = sourceType
    .replace(/_/g, " ")
    .replace(/\bgoogle business profile\b/i, "Google")
    .replace(/\bfacebook pages\b/i, "Facebook")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `<span class="ease-review-source" data-ease-review-source="${attr(sourceType)}">${text(label)}</span>`;
}

function renderReviewCard(
  review: PublishedReviewItem,
  showRating: boolean,
  showSource: boolean,
): string {
  const ratingHtml = showRating ? `\n      ${renderStars(review.rating)}` : "";
  const bodyHtml = review.text
    ? `\n      <blockquote class="ease-review-quote">${text(review.text)}</blockquote>`
    : "";
  const authorHtml = review.authorName
    ? `<cite class="ease-review-author">${text(review.authorName)}</cite>`
    : "";
  const dateHtml = review.publishedAt
    ? `<time class="ease-review-date" datetime="${attr(review.publishedAt)}">${text(review.publishedAt)}</time>`
    : "";
  const sourceHtml = showSource && review.sourceType
    ? renderSourceBadge(review.sourceType)
    : "";

  const metaParts = [authorHtml, dateHtml, sourceHtml].filter(Boolean);
  const footerHtml = metaParts.length > 0
    ? `\n      <footer class="ease-review-footer">${metaParts.join(" &middot; ")}</footer>`
    : "";

  return `    <article class="ease-review-card" data-ease-review-id="${attr(review.id)}">${ratingHtml}${bodyHtml}${footerHtml}
    </article>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as ReviewsGridProps;

  const heading = p.heading ?? DEFAULT_HEADING;
  const reviews = Array.isArray(p.reviews) ? p.reviews : [];
  const columns = Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, p.columns ?? DEFAULT_COLUMNS));
  // Block registry declares showRating/showSource as `'yes' | 'no'` strings
  // (see block-registry.ts), but the renderer interface allows boolean for
  // template seeds and AI-generated callers. Treat any of `false`, `"no"`,
  // `"false"`, `"0"` as off; everything else (`true`, `"yes"`, `undefined`)
  // is on. Critical because the editor toggle saves `"no"` and a naive
  // `!== false` check leaves the badge visible.
  const isOff = (value: unknown): boolean =>
    value === false || value === "no" || value === "false" || value === "0";
  const showRating = !isOff(p.showRating);
  const showSource = !isOff(p.showSource);
  const emptyStateMessage = p.emptyStateMessage ?? DEFAULT_EMPTY_STATE;
  const tenantAttr = p.tenantId ? ` data-ease-tenant-id="${attr(p.tenantId)}"` : "";

  const headingHtml = `<h2 class="ease-reviews-heading">${text(heading)}</h2>`;

  if (reviews.length === 0) {
    return `<section class="ease-section ease-reviews-grid ease-reviews-grid--empty" aria-label="${attr(heading)}"${tenantAttr}>
  ${headingHtml}
  <p class="ease-reviews-empty" role="status">${text(emptyStateMessage)}</p>
</section>`;
  }

  const cardsHtml = reviews
    .map((review) => renderReviewCard(review, showRating, showSource))
    .join("\n");

  return `<section class="ease-section ease-reviews-grid" aria-label="${attr(heading)}"${tenantAttr}>
  ${headingHtml}
  <div class="ease-reviews-cards" style="display: grid; grid-template-columns: repeat(${attr(String(columns))}, 1fr); gap: var(--ease-spacing-lg, 2rem);">
${cardsHtml}
  </div>
</section>`;
}

registerRenderer("reviews-grid", { render });
